import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ApropriacaoEntrada, CriarCifDto, AtualizarCifDto } from '@triade/contracts';
import { useAtualizarCif, useCif, useCriarCif, useExcluirCif } from '@/api/cifs';
import { useAnaliticos } from '@/api/centros-custo';
import { useFornecedores } from '@/api/fornecedores';
import { useItensCif } from '@/api/itens-cif';
import { usePode } from '@/api/me';
import { ApropriacaoLeitura, ApropriacaoPorCentro, type ErroApropriacao } from '@/components/apropriacao';
import { ErroLista } from '@/components/cargos';
import { MonthPicker } from '@/components/ui/month-picker';
import { avisarDesfazer, HistoricoRegistro } from '@/components/desfazer';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { competenciaAtual } from '@/components/centros-custo/tipos';
import { ApiError } from '@/lib/api';
import { decimalDigitado } from '@/lib/format';
import { cn } from '@/lib/utils';
import { apropriacaoParaEntrada } from '@/components/imobilizados/imobilizado-form';
import { cifVazio, deCif, paraCorpo, preenchimentoDoItem, validarCorpo, type CampoCif, type CifFormEntrada, type ErroCampoCif } from './cif-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

export interface BuscaCifs {
  comp?: string;
  q?: string;
  id?: string;
}

/** Registro de um lancamento de CIF (ou novo). Sem `custo.ver` nao ha campo de valor e a criacao nao e possivel. */
export function CifDetalhe({ id, busca, podeEditar }: { id: string | undefined; busca: BuscaCifs; podeEditar: boolean }) {
  const navigate = useNavigate();
  const { pode } = usePode();
  const verCusto = pode('custo.ver');
  const doc = useCif(id);
  const criar = useCriarCif();
  const atualizar = useAtualizarCif();
  const excluir = useExcluirCif();
  const qc = useQueryClient();
  const fornecedores = useFornecedores({}, { ativo: verCusto });
  /** Itens e centros so servem ao lancamento novo (criacao a partir de item ou atalho "vai direto para o centro"). */
  const itensCif = useItensCif({ ativo: true, limit: 200 });
  const centros = useAnaliticos(false, !id);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [criadoId, setCriadoId] = useState<string | null>(null);
  /** Apropriacao editada na tela (null = nada editado; vale a gravada). `completa` vem do componente. */
  const [apropriacao, setApropriacao] = useState<{ valor: ApropriacaoEntrada | null; completa: boolean } | null>(null);
  const apropriacaoAlterada = apropriacao !== null;
  const [chaveApropriacao, setChaveApropriacao] = useState(0);
  const apropriacaoGravada = useMemo(() => apropriacaoParaEntrada(doc.data?.apropriacao ?? null), [doc.data]);
  const [erroApropriacao, setErroApropriacao] = useState<ErroApropriacao | null>(null);

  const base = useMemo<CifFormEntrada>(() => (doc.data ? deCif(doc.data) : cifVazio(busca.comp ?? competenciaAtual())), [doc.data, busca.comp]);
  const form = useForm<CifFormEntrada>({ defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields, errors } = form.formState;
  /** Sujo = campos do formulario OU so a apropriacao (que nao passa pelo RHF). */
  const dirty = isDirty || apropriacaoAlterada;
  const valorDigitado = form.watch('valor');
  const itemCifId = form.watch('itemCifId');
  const centroEscolhido = form.watch('centroId');
  const itemEscolhido = (itensCif.data ?? []).find((i) => i.id === itemCifId);
  const centroEscolhidoRotulo = (centros.data ?? []).find((c) => c.id === centroEscolhido);
  /** Base da coluna R$ da apropriacao: so o texto digitado reescrito como decimal (nenhuma conta). */
  const valorBase = verCusto ? (decimalDigitado(valorDigitado) ?? undefined) : undefined;

  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const bloqueio = useBlockerDirty(dirty && criadoId === null);
  useEffect(() => {
    if (criadoId) void navigate({ to: '/custos/cifs', search: (a) => ({ ...a, id: criadoId }), replace: true });
  }, [criadoId, navigate]);

  const cif = doc.data;
  const bloqueada = cif?.bloqueada === true;
  const salvando = criar.isPending || atualizar.isPending || excluir.isPending;
  const alterados = Object.keys(dirtyFields).length + (apropriacaoAlterada ? 1 : 0);
  const lista = { to: '/custos/cifs' as const, search: { ...(busca.comp ? { comp: busca.comp } : {}), ...(busca.q ? { q: busca.q } : {}) } };

  /** Fornecedor gravado que nao veio na lista (ex.: sem custo.ver a lista nao carrega): mostra o nome da API. */
  /** Escolher item ou centro: a apropriacao digitada antes some (a API recebe so o item ou so o centro). */
  function aoEscolherItem(itemId: string) {
    const item = (itensCif.data ?? []).find((i) => i.id === itemId);
    if (item) {
      const preenchido = preenchimentoDoItem(item);
      form.setValue('identificador', preenchido.identificador, { shouldDirty: true });
      form.setValue('descricao', preenchido.descricao, { shouldDirty: true });
    }
    setApropriacao(null);
    setErroApropriacao(null);
    setChaveApropriacao((k) => k + 1);
  }

  function aoEscolherCentro() {
    setApropriacao(null);
    setErroApropriacao(null);
    setChaveApropriacao((k) => k + 1);
  }

  const opcoesFornecedor = useMemo(() => {
    const itens = (fornecedores.data ?? []).map((f) => ({ id: f.id, nome: f.nome }));
    if (cif?.fornecedorId && !itens.some((f) => f.id === cif.fornecedorId)) itens.push({ id: cif.fornecedorId, nome: cif.fornecedorNome ?? 'Fornecedor' });
    return itens;
  }, [fornecedores.data, cif]);

  function apontar(erro: ErroCampoCif) {
    setErroSalvar(erro.mensagem);
    if (erro.campo) form.setError(erro.campo as CampoCif, { message: erro.mensagem });
  }

  async function gravar(v: CifFormEntrada) {
    setErroSalvar(null);
    setErroApropriacao(null);
    // MANUAL precisa fechar 100% (e ter linhas completas): editada e incompleta nao vai para a API.
    if (apropriacao && !apropriacao.completa) {
      setErroApropriacao({ code: 'APROPRIACAO_INCOMPLETA', message: 'A apropriação precisa fechar 100% (e ter todas as linhas completas) para salvar.' });
      return;
    }
    const montado = paraCorpo(v, {
      comCusto: verCusto,
      fornecedor: verCusto,
      apropriacao: apropriacao ? apropriacao.valor : apropriacaoGravada,
    });
    if (!montado.ok) return apontar(montado.erro);
    const validado = validarCorpo(montado.corpo, id ? 'atualizar' : 'criar');
    if (!validado.ok) return apontar(validado.erro);
    try {
      if (id) {
        const gravado = await atualizar.mutateAsync({ id, ...(validado.dados as AtualizarCifDto) });
        void avisarDesfazer({ recurso: 'cifs', chave: id, qc });
        form.reset(deCif(gravado));
      } else {
        const criado = await criar.mutateAsync(validado.dados as CriarCifDto);
        form.reset(deCif(criado));
        setApropriacao(null);
        setChaveApropriacao((k) => k + 1);
        setSalvoEm(new Date());
        setCriadoId(criado.id);
        return;
      }
      // Gravada: a apropriacao volta a ser a do servidor (rascunho novo, com o que foi gravado).
      setApropriacao(null);
      setChaveApropriacao((k) => k + 1);
      setSalvoEm(new Date());
    } catch (e) {
      setErroSalvar(mensagemApi(e));
      if (e instanceof ApiError) {
        for (const i of e.body?.issues ?? []) {
          const campo = i.path.split('.')[0] as CampoCif;
          if (campo in base && !i.path.startsWith('apropriacao')) form.setError(campo, { message: i.message });
        }
        const issueApropriacao = e.body?.issues?.find((i) => i.path.startsWith('apropriacao'));
        if (issueApropriacao || e.code?.includes('SOMA') || e.code?.startsWith('APROPRIACAO')) {
          setErroApropriacao({ code: e.code ?? 'APROPRIACAO', message: issueApropriacao?.message ?? e.message });
        }
      }
    }
  }

  async function remover() {
    if (!id) return;
    setErroSalvar(null);
    try {
      await excluir.mutateAsync(id);
      await navigate({ ...lista, replace: true });
    } catch (e) {
      setErroSalvar(mensagemApi(e));
    }
  }

  if (id && doc.isError) return <section className="rounded-2xl border border-border bg-card p-5"><ErroLista erro={doc.error} onTentar={() => void doc.refetch()} titulo="Não foi possível abrir o lançamento de CIF" /></section>;
  if (id && !cif) return <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-muted" role="status">Carregando lançamento de CIF…</section>;
  if (!id && !verCusto) {
    return (
      <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-body" role="status">
        Seu acesso não permite cadastrar lançamentos de CIF: o valor é obrigatório e só quem tem custo.ver o informa.
      </section>
    );
  }

  const edicaoBloqueada = !podeEditar || bloqueada;

  return (
    <>
      <section aria-label="Dados do lançamento de CIF" className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="text-body font-semibold">{cif ? cif.descricao : 'Novo lançamento de CIF'}</h2>
          {id && <HistoricoRegistro recurso="cifs" chave={id} />}
        </div>
        {!podeEditar && (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Somente leitura.</b> Seu acesso permite consultar os lançamentos de CIF, não alterá-los.
          </p>
        )}
        {bloqueada && (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            Esta CIF já foi rateada (rateio fechado) e não pode ser alterada nem excluída.
          </p>
        )}

        <fieldset disabled={edicaoBloqueada} className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
          {!id && (
            <>
              <div>
                <label htmlFor="cif-item" className={ROTULO}>Item de CIF</label>
                <select id="cif-item" className={CAMPO} {...form.register('itemCifId', { onChange: (e) => aoEscolherItem(e.target.value) })}>
                  <option value="">Sem item (lançamento direto)</option>
                  {(itensCif.data ?? []).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cif-centro" className={ROTULO}>Centro (vai direto)</label>
                <select id="cif-centro" className={CAMPO} {...form.register('centroId', { onChange: () => aoEscolherCentro() })}>
                  <option value="">Sem centro direto</option>
                  {(centros.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {`${c.caminho} — ${c.nome}`}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div>
            <label htmlFor="cif-identificador" className={ROTULO}>Identificador</label>
            <input id="cif-identificador" maxLength={60} className={CAMPO} aria-invalid={errors.identificador ? true : undefined} aria-describedby={errors.identificador ? 'cif-identificador-erro' : undefined} {...form.register('identificador')} />
            <MensagemCampo id="cif-identificador-erro" mensagem={errors.identificador?.message} />
          </div>
          <div>
            <label htmlFor="cif-documento" className={ROTULO}>Nº documento</label>
            <input id="cif-documento" maxLength={60} className={CAMPO} aria-invalid={errors.numeroDocumento ? true : undefined} {...form.register('numeroDocumento')} />
            <MensagemCampo id="cif-documento-erro" mensagem={errors.numeroDocumento?.message} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="cif-descricao" className={ROTULO}>Descrição</label>
            <input id="cif-descricao" maxLength={200} className={CAMPO} aria-invalid={errors.descricao ? true : undefined} aria-describedby={errors.descricao ? 'cif-descricao-erro' : undefined} {...form.register('descricao')} />
            <MensagemCampo id="cif-descricao-erro" mensagem={errors.descricao?.message} />
          </div>
          <div>
            <label htmlFor="cif-competencia" className={ROTULO}>Competência</label>
            <Controller
              control={form.control}
              name="competencia"
              render={({ field }) => (
                <MonthPicker
                  id="cif-competencia"
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={field.disabled}
                  aria-invalid={errors.competencia ? true : undefined}
                  aria-describedby={errors.competencia ? 'cif-competencia-erro' : undefined}
                  className="w-full justify-between"
                />
              )}
            />
            <MensagemCampo id="cif-competencia-erro" mensagem={errors.competencia?.message} />
          </div>
          <div>
            <label htmlFor="cif-vencimento" className={ROTULO}>Vencimento</label>
            <input id="cif-vencimento" type="date" className={`${CAMPO} tabular`} aria-invalid={errors.vencimento ? true : undefined} aria-describedby={errors.vencimento ? 'cif-vencimento-erro' : undefined} {...form.register('vencimento')} />
            <MensagemCampo id="cif-vencimento-erro" mensagem={errors.vencimento?.message} />
          </div>
          {verCusto && (
            <div>
              <label htmlFor="cif-valor" className={ROTULO}>Valor (R$)</label>
              <input id="cif-valor" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.valor ? true : undefined} aria-describedby={errors.valor ? 'cif-valor-erro' : undefined} {...form.register('valor')} />
              <MensagemCampo id="cif-valor-erro" mensagem={errors.valor?.message} />
            </div>
          )}
          <div>
            <label htmlFor="cif-fornecedor" className={ROTULO}>Fornecedor</label>
            <select id="cif-fornecedor" disabled={!verCusto} className={CAMPO} aria-invalid={errors.fornecedorId ? true : undefined} {...form.register('fornecedorId')}>
              <option value="">Sem fornecedor</option>
              {opcoesFornecedor.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                </option>
              ))}
            </select>
            <MensagemCampo id="cif-fornecedor-erro" mensagem={errors.fornecedorId?.message} />
          </div>
        </fieldset>

        <div className="mt-5">
          <h4 className="mb-2 text-small font-semibold">Apropriação por centro de custo</h4>
          {!id && centroEscolhido !== '' ? (
            <p role="status" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
              Vai direto para {centroEscolhidoRotulo ? `${centroEscolhidoRotulo.caminho} — ${centroEscolhidoRotulo.nome}` : 'o centro escolhido'}: 100% do valor.
            </p>
          ) : !id && itemEscolhido ? (
            <div className="space-y-2">
              <p className="text-small text-ink-secondary">
                Apropriação padrão herdada do item <b>{itemEscolhido.nome}</b>. A API aplica na criação.
              </p>
              <ApropriacaoLeitura apropriacao={itemEscolhido.apropriacao} />
            </div>
          ) : (
            <div className={cn('mt-3', edicaoBloqueada && 'opacity-80')}>
              <ApropriacaoPorCentro
                key={`${id ?? 'novo'}-${chaveApropriacao}-${cif?.id ?? ''}`}
                value={apropriacaoGravada}
                onChange={(valor, situacao) => setApropriacao({ valor, completa: situacao?.completa ?? valor !== null })}
                resolvida={cif?.apropriacao ?? null}
                referTo="CIF"
                disabled={edicaoBloqueada || salvando}
                erro={erroApropriacao}
                valorBase={valorBase}
              />
            </div>
          )}
        </div>
      </section>

      {podeEditar && !bloqueada && (
        <RecordToolbar
          dirty={dirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erroSalvar}
          novo={!id}
          canPrev={false}
          canNext={false}
          alteradosCount={dirty ? alterados : undefined}
          onList={() => void navigate({ ...lista, replace: true })}
          onSave={() => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))()}
          onNew={() => void navigate({ to: '/custos/cifs', search: (a) => ({ ...a, id: 'novo' }) })}
          onDelete={id ? () => void remover() : undefined}
          excluirTitulo="Excluir lançamento de CIF"
          excluirDescricao="O lançamento sai da lista e a apropriação é removida."
          excluirConfirmar="Excluir"
          onUndo={() => {
            form.reset(base);
            setErroSalvar(null);
            setErroApropriacao(null);
            setApropriacao(null);
            setChaveApropriacao((k) => k + 1);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}
